import Vue from 'vue'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from './components/nuxt-error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_plugin_0dcd0d5e from 'nuxt_plugin_plugin_0dcd0d5e' // Source: ./vuetify/plugin.js (mode: 'all')
import nuxt_plugin_appinit_2af3aa6e from 'nuxt_plugin_appinit_2af3aa6e' // Source: ../plugins/app_init.ts (mode: 'all')
import nuxt_plugin_vuespinner_de9f107a from 'nuxt_plugin_vuespinner_de9f107a' // Source: ../plugins/vue_spinner.ts (mode: 'all')
import nuxt_plugin_fontawesome_b8db3322 from 'nuxt_plugin_fontawesome_b8db3322' // Source: ../plugins/fontawesome.ts (mode: 'all')
import nuxt_plugin_vuetify_d6afc056 from 'nuxt_plugin_vuetify_d6afc056' // Source: ../plugins/vuetify.ts (mode: 'all')
import nuxt_plugin_dayjs_66ce609e from 'nuxt_plugin_dayjs_66ce609e' // Source: ../plugins/dayjs.ts (mode: 'all')
import nuxt_plugin_globalcomponent_31279cf6 from 'nuxt_plugin_globalcomponent_31279cf6' // Source: ../plugins/global_component.ts (mode: 'all')
import nuxt_plugin_library_6826d752 from 'nuxt_plugin_library_6826d752' // Source: ../plugins/library.js (mode: 'all')
import nuxt_plugin_logger_057b84e7 from 'nuxt_plugin_logger_057b84e7' // Source: ../plugins/logger.ts (mode: 'all')
import nuxt_plugin_config_3fddc1f5 from 'nuxt_plugin_config_3fddc1f5' // Source: ../plugins/config.ts (mode: 'all')
import nuxt_plugin_modal_640f2a54 from 'nuxt_plugin_modal_640f2a54' // Source: ../plugins/modal.ts (mode: 'all')
import nuxt_plugin_filters_2dd71148 from 'nuxt_plugin_filters_2dd71148' // Source: ../plugins/filters.ts (mode: 'all')
import nuxt_plugin_eventbus_f6b2df30 from 'nuxt_plugin_eventbus_f6b2df30' // Source: ../plugins/event_bus.ts (mode: 'all')
import nuxt_plugin_websocket_0f1bd07c from 'nuxt_plugin_websocket_0f1bd07c' // Source: ../plugins/websocket.ts (mode: 'all')
import nuxt_plugin_directive_c8d99218 from 'nuxt_plugin_directive_c8d99218' // Source: ../plugins/directive.ts (mode: 'all')
import nuxt_plugin_reWriteCard_1e60bff2 from 'nuxt_plugin_reWriteCard_1e60bff2' // Source: ../plugins/reWriteCard.ts (mode: 'all')
import nuxt_plugin_felica_ee4bcb46 from 'nuxt_plugin_felica_ee4bcb46' // Source: ../plugins/felica.ts (mode: 'all')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":true,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

async function createApp (ssrContext) {
  const router = await createRouter(ssrContext)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"title":"みなLib","htmlAttrs":{"lang":"ja"},"meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width, initial-scale=1"},{"hid":"description","name":"description","content":"mina-lib-front project"}],"link":[{"rel":"icon","type":"image\u002Fx-icon","href":"\u002Ffavicon.ico"}],"style":[],"script":[]},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  const inject = function (key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Plugin execution

  if (typeof nuxt_plugin_plugin_0dcd0d5e === 'function') {
    await nuxt_plugin_plugin_0dcd0d5e(app.context, inject)
  }

  if (typeof nuxt_plugin_appinit_2af3aa6e === 'function') {
    await nuxt_plugin_appinit_2af3aa6e(app.context, inject)
  }

  if (typeof nuxt_plugin_vuespinner_de9f107a === 'function') {
    await nuxt_plugin_vuespinner_de9f107a(app.context, inject)
  }

  if (typeof nuxt_plugin_fontawesome_b8db3322 === 'function') {
    await nuxt_plugin_fontawesome_b8db3322(app.context, inject)
  }

  if (typeof nuxt_plugin_vuetify_d6afc056 === 'function') {
    await nuxt_plugin_vuetify_d6afc056(app.context, inject)
  }

  if (typeof nuxt_plugin_dayjs_66ce609e === 'function') {
    await nuxt_plugin_dayjs_66ce609e(app.context, inject)
  }

  if (typeof nuxt_plugin_globalcomponent_31279cf6 === 'function') {
    await nuxt_plugin_globalcomponent_31279cf6(app.context, inject)
  }

  if (typeof nuxt_plugin_library_6826d752 === 'function') {
    await nuxt_plugin_library_6826d752(app.context, inject)
  }

  if (typeof nuxt_plugin_logger_057b84e7 === 'function') {
    await nuxt_plugin_logger_057b84e7(app.context, inject)
  }

  if (typeof nuxt_plugin_config_3fddc1f5 === 'function') {
    await nuxt_plugin_config_3fddc1f5(app.context, inject)
  }

  if (typeof nuxt_plugin_modal_640f2a54 === 'function') {
    await nuxt_plugin_modal_640f2a54(app.context, inject)
  }

  if (typeof nuxt_plugin_filters_2dd71148 === 'function') {
    await nuxt_plugin_filters_2dd71148(app.context, inject)
  }

  if (typeof nuxt_plugin_eventbus_f6b2df30 === 'function') {
    await nuxt_plugin_eventbus_f6b2df30(app.context, inject)
  }

  if (typeof nuxt_plugin_websocket_0f1bd07c === 'function') {
    await nuxt_plugin_websocket_0f1bd07c(app.context, inject)
  }

  if (typeof nuxt_plugin_directive_c8d99218 === 'function') {
    await nuxt_plugin_directive_c8d99218(app.context, inject)
  }

  if (typeof nuxt_plugin_reWriteCard_1e60bff2 === 'function') {
    await nuxt_plugin_reWriteCard_1e60bff2(app.context, inject)
  }

  if (typeof nuxt_plugin_felica_ee4bcb46 === 'function') {
    await nuxt_plugin_felica_ee4bcb46(app.context, inject)
  }

  // If server-side, wait for async component to be resolved first
  if (process.server && ssrContext && ssrContext.url) {
    await new Promise((resolve, reject) => {
      router.push(ssrContext.url, resolve, () => {
        // navigated to a different route in router guard
        const unregister = router.afterEach(async (to, from, next) => {
          ssrContext.url = to.fullPath
          app.context.route = await getRouteData(to)
          app.context.params = to.params || {}
          app.context.query = to.query || {}
          unregister()
          resolve()
        })
      })
    })
  }

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
